import {
  ArrowUpRight, Check, ChevronDown, ChevronRight, ChevronUp, Compass, Eye,
  Info,
  ThumbsDown, ThumbsUp, User,
} from 'react-feather';
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import styled, { css } from 'styled-components';

import { CurrentUserFieldsFragment, Locale } from '__generated__/graphql';

import { Event, Participant } from '../Dashboard/helpers';
import { EventInfo } from '../../common/Common/EventHeader';
import { GridAlignment, Size, UIProps } from '../../theme/mixins';
import { Project } from '../useProject';
import { resizedImageUrl } from '../../common/helpers';
import { useEditLocaleHandler } from './useEditLocaleHandler';
import { useNavigation } from './useNavigation';
import AddReviewMutation from '../../common/AddReviewMutation';
import AtletaLogo, { AtletaLogoElement } from '../../common/Common/AtletaLogo';
import LanguageSelector from '../../common/Common/LanguageSelector';
import OptionsDropdown, { ExternalOption } from '../../common/Common/OptionsDropdown';
import SC from '../../common/UI/SC';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';

interface SectionProps {
  title?: ReactNode;
  header?: ReactNode;
  count?: number;
  secondary?: boolean;
  dashed?: boolean;
  id?: string;
  intro?: ReactNode;
  active?: boolean;
  spacing?: Size;
  sc?: UIProps;
  children: ReactNode;
}

export const Section = ({
  title, header, count, secondary = false, dashed = false, id, intro, active, spacing = 'sm', sc,
  children,
}: SectionProps) => (
  <SectionContainer dashed={dashed} id={id} sc={sc}>
    {(title || intro || header) && (
      <UI.Div>
        {title && <SectionTitle count={count}>{title}</SectionTitle>}
        {intro && <UI.Div sc={{ color: 'gray.500' }}>{intro}</UI.Div>}
        {header}
      </UI.Div>
    )}

    <UI.Card
      sc={{
        hasPanels: true,
        background: secondary ? 'gray.300/20' : undefined,
        spacing,
        active,
        transitionPadding: true,
      }}
    >
      {children}
    </UI.Card>
  </SectionContainer>
);

interface SectionTitleProps {
  children: ReactNode;
  loading?: boolean;
  count?: number;
}

export const SectionTitle = ({ children, loading, count }: SectionTitleProps) => {
  const { formatNumber } = useLocale();

  return (
    <UI.H3 sc={{ fontSize: 3, fontWeight: 600, flex: { alignItems: 'center' } }}>
      <UI.Span sc={{ mr: 1.5 }}>
        {children}
      </UI.Span>
      {loading && (
        <UI.Loader sc={{ brand: 'gray.500' }} />
      )}
      {!loading && typeof count !== 'undefined' && (
        <UI.Small>
          <UI.Badge sc={{ color: 'gray.500' }}>
            {formatNumber(count)}
          </UI.Badge>
        </UI.Small>
      )}
    </UI.H3>
  );
};

interface NavItemProps extends SC<UIProps> {
  to?: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  id?: string;
  target?: string;
  rel?: string;
  title?: string;
  icon?: ReactNode;
  alignVertical?: GridAlignment;
  children: ReactNode;
}

export const NavItem = ({
  to, href, onClick, active, id, target, rel, title, icon, alignVertical, sc, children,
}: NavItemProps) => (
  <UI.LinkBlock
    to={to}
    href={href}
    onClick={onClick}
    id={id}
    target={target}
    rel={rel}
    title={title}
    sc={{ pr: [7, 9], relative: true, ...sc }}
  >
    {children}
    <NavItemIcon alignVertical={alignVertical} sc={{ mr: active ? 1 : undefined }}>
      {typeof icon === 'undefined' ? <ChevronRight /> : icon}
    </NavItemIcon>
  </UI.LinkBlock>
);

const NavItemIcon = styled(UI.Icon)<{ alignVertical?: GridAlignment; }>`
  ${({ alignVertical = 'top', theme }) => css`
    font-size: 1.25rem;
    color: ${theme.colors.gray[500]};
    transition: all 0.1s ease-in-out;
    position: absolute;
    height: 20px;
    top: ${alignVertical === 'center' ? '50%' : '13px'};
    margin-top: ${alignVertical === 'center' ? '-10px' : 0};
    right: ${theme.gutter * 0.5}px;

    svg {
      stroke-width: 1.5px;
    }

    @media ${theme.devices.md} {
      top: ${alignVertical === 'center' ? '50%' : '18px'};
      right: ${theme.gutter * 0.75}px;
    }
  `}
`;

interface SectionState {
  [key: string]: boolean;
}

interface UseDropdownSectionStateOptions {
  initialState?: SectionState | boolean;
  onOpen?: (key: string) => void;
  onClose?: (key: string) => void;
}

/**
 * Provides state and handlers to control <DropdownSection />.
 */
export const useDropdownSectionState = ({
  initialState = {}, onOpen, onClose,
}: UseDropdownSectionStateOptions = {}) => {
  const defaultKey = 'default';

  const [open, setOpen] = useState<SectionState>(
    typeof initialState === 'boolean' ? { [defaultKey]: initialState } : initialState,
  );

  const isOpen = useCallback((key: string = defaultKey) => !!open[key], [open]);

  const openSection = useCallback((key: string = defaultKey) => {
    setOpen((open) => ({ ...open, [key]: true }));
    onOpen?.(key);
  }, [onOpen]);

  const closeSection = useCallback((key: string = defaultKey) => {
    setOpen((open) => ({ ...open, [key]: false }));
    onClose?.(key);
  }, [onClose]);

  const toggleSection = useCallback((key: string = defaultKey) => {
    const isOpen = !open[key];

    setOpen({ ...open, [key]: isOpen });

    if (isOpen) {
      onOpen?.(key);
    } else {
      onClose?.(key);
    }
  }, [open, onOpen, onClose]);

  return {
    isOpen,
    openSection,
    closeSection,
    toggleSection,
  };
};

const DropdownSectionContent = styled(UI.Div)`
  ${({ theme }) => css`
    background: ${theme.colors.gray[50]};
    border-bottom-left-radius: ${theme.borderRadiuses.xl}px;
    border-bottom-right-radius: ${theme.borderRadiuses.xl}px;

    > * + * {
      border-top: 1px solid ${theme.colors.gray[150]};
    }
  `}
`;

interface DropdownSectionrops {
  preview: ReactNode;
  body: ReactNode;
  title?: string;
  open?: boolean;
  onToggle: Dispatch<SetStateAction<boolean>>;
  id?: string;
  sc?: UIProps;
}

export const DropdownSection = ({
  preview, body, title, open, onToggle, id, sc,
}: DropdownSectionrops) => (
  <Section spacing={open ? 'md' : 'sm'} id={id} active={open}>
    <NavItem
      title={title}
      onClick={body ? () => onToggle((open) => !open) : undefined}
      icon={body ? (!open ? <ChevronDown /> : <ChevronUp />) : null}
      active={open}
      sc={sc}
    >
      {preview}
    </NavItem>
    <UI.Div
      sc={{ padding: 0 }}
      style={{
        opacity: !open ? 0 : undefined,
        transition: 'all 0.15s ease-in-out',
      }}
      id={id ? `${id}_Body` : undefined}
    >
      <UI.AnimateHeight isVisible={open}>
        <DropdownSectionContent>
          {body}
        </DropdownSectionContent>
      </UI.AnimateHeight>
    </UI.Div>
  </Section>
);

interface SectionContainerProps extends SC<UIProps> {
  dashed?: boolean;
}

export const SectionContainer = styled(UI.GridContainer)<SectionContainerProps>`
  ${({ dashed, theme }) => css`
    grid-gap: ${theme.gutter * 0.75}px;

    ${UI.H4} {
      font-weight: 600;

      ${UI.Icon} svg {
        stroke-width: 2.5;
      }
    }

    ${dashed && css`
      > ${UI.Card} > * + * {
        border-top: 2px dashed ${theme.colors.gray[150]};
      }
    `}

    > ${UI.Card} > *:hover ${NavItemIcon} {
      margin-right: -3px;
      color: ${theme.colors.gray[800]};
    }

    > ${UI.Card} {
      border-radius: ${theme.borderRadiuses.xl}px;

      > :first-child {
        border-top-left-radius: ${theme.borderRadiuses.xl}px;
        border-top-right-radius: ${theme.borderRadiuses.xl}px;
      }

      > :last-child {
        border-bottom-left-radius: ${theme.borderRadiuses.xl}px;
        border-bottom-right-radius: ${theme.borderRadiuses.xl}px;
      }
    }
  `}
`;

interface BreadcrumbsProps {
  hideHome?: boolean;
  children?: ReactNode | ReactNode[];
}

export const Breadcrumbs = ({ hideHome, children }: BreadcrumbsProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();

  // The children of the Delimit component have to be a flattened array for it to work
  const breadcrumbs = [
    ...!hideHome ? [
      <UI.Link to={r('Portal')} key="home">
        {t('my_account')}
      </UI.Link>,
    ] : [],
    ...Array.isArray(children) ? children : [children],
  ];

  return (
    <UI.Div sc={{ small: true }}>
      <UI.Delimit
        delimiter={<UI.Icon sc={{ muted: true, mx: 1 }}><ChevronRight /></UI.Icon>}
      >
        {breadcrumbs}
      </UI.Delimit>
    </UI.Div>
  );
};

export const Container = styled(UI.Div)<{ size?: 'sm' | 'lg'; }>`
  ${({ size = 'sm', theme }) => css`
    min-width: 360px;
    max-width: ${size === 'sm' ? `${theme.breakpoints.md}px` : `${theme.breakpoints.xl}px`};
    width: 100%;
    margin: 0 auto;
    display: grid;
    grid-gap: ${theme.gutter}px;

    ${size === 'sm' && css`
      padding: ${theme.gutter * 1.25}px ${theme.gutter * (2 / 3)}px;
    `}

    ${size === 'lg' && css`
      padding: ${theme.gutter * 1.5}px ${theme.gutter * (2 / 3)}px;
    `}

    @media ${theme.devices.md} {
      ${size === 'sm' && css`
        padding: ${theme.gutter * 1.5}px;
      `}

      ${size === 'lg' && css`
        padding: ${theme.gutter * 2}px;
      `}
    }

    > ${UI.HR} {
      margin: 0;
    }
  `}
`;

export const Wrapper = styled(UI.FlexContainer)`
  padding-top: 48px;
  flex-direction: column;
  min-height: 100vh;
  align-items: space-between;
`;

interface FillRequiredFieldsProps {
  onClick: () => void;
}

export const FillRequiredFields = ({ onClick }: FillRequiredFieldsProps) => {
  const { t } = useTranslation();

  return (
    <UI.FadeIn>
      <UI.Warning>
        <UI.A
          onClick={onClick}
          sc={{ secondary: true }}
          style={{ fontWeight: 500 }}
          role="button"
        >
          {t('fill_required_fields')}
          {' '}
          <UI.Icon>
            <ArrowUpRight />
          </UI.Icon>
        </UI.A>
      </UI.Warning>
    </UI.FadeIn>
  );
};

export interface NavBarProps {
  user?: CurrentUserFieldsFragment | null;
  onChangeLocale?: (locale: Locale) => unknown;
  showFullLogo?: boolean;
  impersonatedUsername?: string;
  showOrganiserButton?: boolean;
  isHomePage?: boolean;
}

export const NavBar = ({
  user, onChangeLocale, showFullLogo = false, impersonatedUsername, showOrganiserButton, isHomePage,
}: NavBarProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { locale } = useLocale();

  const [scrolled, setScrolled] = useState(window.pageYOffset > 0);
  const darkMode = !scrolled && isHomePage;

  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrolled(window.pageYOffset > 0);
    });

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const saveLocale = useEditLocaleHandler();

  const handleChangeLocale = (locale: Locale) => {
    saveLocale?.(locale);
    onChangeLocale?.(locale);
  };

  return (
    <NavBarContainer scrolled={scrolled}>
      <Container size="lg" sc={{ py: 0 }}>
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'center' }}
          style={{ width: '100%', height: '100%' }}
        >
          <UI.Link
            to={showFullLogo ? r('Home') : r('Portal')}
            sc={{ mb: 0.5 }}
            disabled={!!impersonatedUsername}
            title="Atleta"
          >
            <AtletaLogo
              symbolOnly={!showFullLogo}
              height={16}
              textColor={darkMode ? 'white' : undefined}
            />
          </UI.Link>
          <UI.FlexContainer sc={{ spaceX: 1 }}>
            {showOrganiserButton && (!user || user.project_memberships.length === 0) && (
              <UI.AButton
                href={`https://about.atleta.cc/${locale}`}
                sc={{ blank: true, brand: darkMode ? 'secondary.900' : null, size: 'sm' }}
              >
                <UI.Icon>
                  <Info />
                </UI.Icon>
                {' '}
                {t('website.for_organisers')}
              </UI.AButton>
            )}
            {showOrganiserButton && user && user.project_memberships.length > 0 && (
              <OptionsDropdown
                trigger={(
                  <UI.Button sc={{ blank: true, brand: darkMode ? 'secondary.900' : null, size: 'sm' }}>
                    <UI.Icon>
                      <Compass />
                    </UI.Icon>
                    {' '}
                    {t('website.organisation')}
                    {' '}
                    <UI.Icon>
                      <ChevronDown />
                    </UI.Icon>
                  </UI.Button>
                )}
              >
                {user.project_memberships.map((membership) => (
                  <ExternalOption
                    label={membership.project.organisation_name}
                    href={`/b/${membership.project.id}`}
                    key={membership.id}
                  />
                ))}
              </OptionsDropdown>
            )}

            <LanguageSelector
              blank
              brand={darkMode ? 'secondary.900' : undefined}
              hideCurrent
              alignV="bottom"
              alignH="right"
              onChangeLocale={handleChangeLocale}
            />

            <UI.LinkButton
              to={user ? r('Portal') : r('Login')}
              sc={{ size: 'sm', ml: 1, disabled: !!impersonatedUsername, brand: 'primary.600' }}
            >
              {impersonatedUsername && (
                <>
                  <UI.Icon>
                    <Eye />
                  </UI.Icon>
                  {' '}
                  {impersonatedUsername}
                </>
              )}
              {!impersonatedUsername && user && (
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {user.first_name}
                </>
              )}
              {!user && (
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {t('log_in')}
                </>
              )}
            </UI.LinkButton>
          </UI.FlexContainer>
        </UI.FlexContainer>
      </Container>
    </NavBarContainer>
  );
};

interface NavBarContainerProps {
  scrolled?: boolean;
}

export const NavBarContainer = styled(UI.Div)<NavBarContainerProps>`
  ${({ theme, scrolled }) => css`
    height: 49px; // Includes border
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 500;
    font-size: ${13 / 16}rem;
    min-height: 60px;
    border-bottom: 1px solid transparent;
    background: transparent;
    transition: all 0.1s ease-in-out;

    ${scrolled && css`
      background: ${theme.transparentize(theme.mix(theme.colors.white, theme.colors.secondary[25], 0.5), 0.25)};
      box-shadow: ${theme.shadows.xs};
      backdrop-filter: blur(10px);
    `}

    > div {
      height: 100%;
    }

    ${AtletaLogoElement} {
      display: block;
    }
 `}
`;

export interface FooterProps {
  project?: Project;
  participant?: Participant,
}

export const Footer = ({ project, participant }: FooterProps) => {
  const location = useLocation();

  if (!project) {
    return <></>;
  }

  return (
    <>
      <UI.HR />
      <FeedbackForm
        project={project}
        participant={participant}
        meta={JSON.stringify({ userAgent: navigator.userAgent })}
        key={location.pathname}
      />
    </>
  );
};

export interface FeedbackFormProps {
  project: Project;
  participant?: Participant | null;
  meta?: string | null;
}

export const FeedbackForm = ({
  project,
  participant,
  meta,
}: FeedbackFormProps) => {
  const { t } = useTranslation();

  const [addReview, { loading }] = useMutation(
    AddReviewMutation,
  );

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);

  const form = useForm({
    values: {
      comment,
      meta,
    },
  });

  const addPositiveReview = () => {
    setShowComment(true);

    if (rating !== 1) {
      setRating(1);

      addReview({
        variables: {
          input: {
            project: { id: project.id },
            participant: participant ? { id: participant.id } : null,
            rating: 1,
            ...form.values,
            url: window.location.href,
            token: participant?.access_token,
          },
        },
      });
    }
  };

  const addNegativeReview = () => {
    setShowComment(true);

    if (rating !== 0) {
      setRating(0);

      addReview({
        variables: {
          input: {
            project: { id: project.id },
            participant: participant ? { id: participant.id } : null,
            rating: 0,
            ...form.values,
            url: window.location.href,
            token: participant?.access_token,
          },
        },
      });
    }
  };

  const addComment = () => {
    setComment(form.values.comment);

    addReview({
      variables: {
        input: {
          project: { id: project.id },
          participant: participant ? { id: participant.id } : null,
          rating,
          ...form.values,
          url: window.location.href,
          token: participant?.access_token,
        },
      },
    });
  };

  const cancel = () => {
    setShowComment(false);
  };

  return (
    <UI.Div>
      <UI.Div sc={{ textAlign: 'center', color: 'gray.600', small: true }}>
        {t('how_do_you_rate_this_page')}
      </UI.Div>
      <UI.Div sc={{ textAlign: 'center', mt: 1 }}>
        <UI.Button
          onClick={() => addPositiveReview()}
          sc={{
            brand: rating === 1 ? 'success' : 'gray.150',
            color: rating === 1 ? 'white' : 'success',
            muted: rating === 0,
            mr: 1,
          }}
          aria-label={t('thumbs_up')}
        >
          <UI.Icon>
            <ThumbsUp />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => addNegativeReview()}
          sc={{
            brand: rating === 0 ? 'error' : 'gray.150',
            color: rating === 0 ? 'white' : 'error',
            muted: rating === 1,
          }}
          aria-label={t('thumbs_down')}
        >
          <UI.Icon>
            <ThumbsDown />
          </UI.Icon>
        </UI.Button>
      </UI.Div>
      <UI.AnimateHeight isVisible={showComment}>
        <UI.Card sc={{ mt: 2, textAlign: 'left' }}>
          <UI.Form onSubmit={() => addComment()}>
            <UI.FormGrid sc={{ gutter: 0.5 }}>
              <UI.InputGroup>
                <UI.InputLabel htmlFor="Comment">
                  {t('feedback_message_label')}
                </UI.InputLabel>
                <UI.TextArea {...form.textarea('comment')} rows={1} id="Comment" />
              </UI.InputGroup>
              <UI.Div>
                <UI.Button
                  type="submit"
                  sc={{ brand: 'secondary', loading }}
                  disabled={!form.values.comment || form.values.comment === comment}
                >
                  {(!comment || form.values.comment !== comment || loading)
                    ? t('send_feedback')
                    : (
                      <>
                        <UI.Icon>
                          <Check />
                        </UI.Icon>
                        {' '}
                        {t('feedback_sent')}
                      </>
                    )}
                </UI.Button>
                {' '}
                <UI.Button onClick={() => cancel()}>
                  {t('close')}
                </UI.Button>
              </UI.Div>
            </UI.FormGrid>
          </UI.Form>
        </UI.Card>
      </UI.AnimateHeight>
    </UI.Div>
  );
};

interface EventHeaderProps {
  event: Event;
}

export const EventHeader = ({ event }: EventHeaderProps) => {
  const { locale } = useLocale();

  return (
    <UI.GridContainer sc={{ columns: '60px 1fr', gutter: 0.5, alignVertical: 'center' }}>
      <UI.Div>
        <UI.A href={`${event.checkout_url}?locale=${locale}`}>
          <UI.ProfilePic
            sc={{
              background: event.logo ? 'white' : undefined,
              size: 60,
            }}
          >
            {event.logo && (
              <img
                src={resizedImageUrl(event.logo.url, 300)}
                alt={event.title}
              />
            )}
            {!event.logo && (
              <UI.Span><UI.Span>{event.title.charAt(0)}</UI.Span></UI.Span>
            )}
          </UI.ProfilePic>
        </UI.A>
      </UI.Div>
      <UI.Div>
        <UI.H1 sc={{ fontSize: 3, mb: 0.5 }}>
          <UI.A href={`${event.checkout_url}?locale=${locale}`} sc={{ color: 'gray.800' }}>
            {event.title}
          </UI.A>
        </UI.H1>
        <EventInfo event={event} />
      </UI.Div>
    </UI.GridContainer>
  );
};

interface FooterNavProps {
  onChangeLocale?: (locale: Locale) => unknown;
}

export const FooterNav = ({ onChangeLocale }: FooterNavProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { locale } = useLocale();
  const saveLocale = useEditLocaleHandler();

  const handleChangeLocale = (locale: Locale) => {
    saveLocale?.(locale);
    onChangeLocale?.(locale);
  };

  return (
    <UI.Div style={{ marginTop: 'auto' }}>
      <UI.HR sc={{ my: 0 }} />
      <Container size="lg">
        <UI.FlexContainer
          sc={{
            justifyContent: [undefined, 'space-between'],
            alignItems: 'center',
            direction: ['column', 'row'],
          }}
        >
          <UI.FlexContainer
            sc={{
              spaceX: [0, 3],
              spaceY: [1, 0],
              justifyContent: ['center', 'start'],
              alignItems: 'center',
              direction: ['column', 'row'],
              small: true,
            }}
          >
            <UI.FlexContainer sc={{ justifyContent: 'center', spaceX: 3 }}>
              <FooterNavLink to={r('Calendar')} sc={{ secondary: true }}>
                {t('website.event_calendar')}
              </FooterNavLink>
              <FooterNavLink to={r('Portal')} sc={{ secondary: true }}>
                {t('website.my_tickets')}
              </FooterNavLink>
              <UI.A
                href={`https://about.atleta.cc/${locale}/participant-support`}
                sc={{ secondary: true }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('website.help')}
              </UI.A>
              <UI.A
                href={`https://about.atleta.cc/${locale}`}
                sc={{ secondary: true }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('website.atleta_for_organisers')}
              </UI.A>
            </UI.FlexContainer>
            <UI.FlexContainer sc={{ justifyContent: 'center', spaceX: 3 }}>
              <UI.A
                href={`https://about.atleta.cc/${locale}/terms-and-conditions`}
                sc={{ secondary: true }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('website.terms')}
              </UI.A>
              <UI.A
                href={`https://about.atleta.cc/${locale}/privacy`}
                sc={{ secondary: true }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('website.privacy')}
              </UI.A>
            </UI.FlexContainer>
          </UI.FlexContainer>
          <UI.Div sc={{ mt: [3, 0] }}>
            <LanguageSelector blank onChangeLocale={handleChangeLocale} />
          </UI.Div>
        </UI.FlexContainer>
      </Container>
    </UI.Div>
  );
};

const FooterNavLink = styled(UI.NavLink)`
  ${({ theme }) => css`
    &.active {
      color: ${theme.colors.gray[400]};
    }
  `}
`;

export default undefined;
